import { confirmSignIn } from "aws-amplify/auth";

import {
  cognitoSignOut,
  ensureCognitoUser,
  getExistingSessionEmail,
  initiateCustomAuth,
} from "./cognitoAuth";

const FIREBASE_TOKEN_EXCHANGE = "FIREBASE_TOKEN_EXCHANGE" as const;

async function selectFirebaseTokenExchangeChallenge(): Promise<void> {
  const result = await confirmSignIn({
    challengeResponse: FIREBASE_TOKEN_EXCHANGE,
    options: {
      clientMetadata: {
        target_custom_challenge: FIREBASE_TOKEN_EXCHANGE,
        sub_challenge_step: "select",
      },
    },
  });

  if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    const challengeParams = result.nextStep.additionalInfo ?? {};
    if (challengeParams.status === "error") {
      throw new Error(challengeParams.message ?? "Firebase token exchange challenge failed");
    }
  }
}

async function respondToFirebaseTokenExchangeChallenge(firebaseIdToken: string): Promise<void> {
  const confirmResult = await confirmSignIn({
    challengeResponse: firebaseIdToken,
    options: {
      clientMetadata: {
        target_custom_challenge: FIREBASE_TOKEN_EXCHANGE,
        sub_challenge_step: "answer",
        "client_metadata:custom_challenge:firebase_token_exchange": "verify",
      },
    },
  });

  if (confirmResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    const challengeParams = confirmResult.nextStep.additionalInfo ?? {};
    if (challengeParams.status === "error") {
      throw new Error(challengeParams.message ?? "Firebase token exchange challenge failed");
    }
  }

  if (!confirmResult.isSignedIn) {
    throw new Error("Firebase token exchange authentication not completed");
  }
}

/**
 * Establishes a Cognito session by exchanging a Firebase ID token.
 *
 * Uses the FIREBASE_TOKEN_EXCHANGE custom auth challenge:
 * 1. Ensures the user exists in Cognito (JIT import via PreSignUp Lambda)
 * 2. Initiates a CUSTOM_WITHOUT_SRP auth flow
 * 3. Selects the FIREBASE_TOKEN_EXCHANGE challenge
 * 4. Responds with the Firebase ID token, which the VerifyAuthChallenge Lambda
 *    validates by calling /api/user/info and matching the user in Cognito
 * 5. On success, Cognito issues tokens — the user now has a Cognito session
 *
 * This is used after password-based Firebase sign-in so that users get both
 * a Firebase session (for backward compatibility) and a Cognito session.
 */
export async function establishCognitoSessionFromFirebaseToken(params: {
  email: string;
  firebaseIdToken: string;
}): Promise<void> {
  const { email, firebaseIdToken } = params;

  await ensureCognitoUser(email);
  await initiateCustomAuth(email);
  await selectFirebaseTokenExchangeChallenge();
  await respondToFirebaseTokenExchangeChallenge(firebaseIdToken);
}

/**
 * Checks whether a valid Cognito session already exists for the given email,
 * and if not, exchanges the Firebase ID token for one. A module-level promise
 * ensures only one exchange is in flight at a time — concurrent callers
 * (e.g., `init.ts` and `useEnsureCognitoSession`) await the same promise
 * instead of racing.
 *
 * Returns `true` if an exchange was actually performed, `false` if the
 * existing Cognito session already matched.
 */
let exchangeInFlight: Promise<boolean> | null = null;

export function exchangeFirebaseTokenForCognitoSessionIfNeeded(params: {
  email: string;
  firebaseIdToken: string;
}): Promise<boolean> {
  if (exchangeInFlight) {
    return exchangeInFlight;
  }

  exchangeInFlight = (async () => {
    const existingCognitoEmail = await getExistingSessionEmail();
    if (existingCognitoEmail?.toLowerCase() === params.email.toLowerCase()) {
      return false;
    }

    if (existingCognitoEmail) {
      await cognitoSignOut();
    }

    await establishCognitoSessionFromFirebaseToken(params);
    return true;
  })().finally(() => {
    exchangeInFlight = null;
  });

  return exchangeInFlight;
}
